// How the Engineering page writes a figure (module 10 U89, U90). Every time is Central European
// (Mike reads CET, never UTC), a share is a share of the team and says so in its column head, and
// "nothing" is one thing everywhere: a quiet 0, never a dash in one column and a zero in the next.
const TZ = 'Europe/Paris';

export const count = (v: number): string => Math.round(v).toLocaleString('en-GB');
/** A net figure can be negative: more removed than added. A true minus sign, so it cannot read as a dash. */
export const net = (v: number): string => (v < 0 ? `−${count(-v)}` : count(v));
export function share(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '';
  const a = Math.abs(v); const s = a >= 10 ? a.toFixed(0) : a >= 1 ? a.toFixed(1) : a.toFixed(2);
  return `${v < 0 ? '−' : ''}${s}%`;
}
export const signed = (v: number): string => (v > 0 ? `+${count(v)}` : v < 0 ? `−${count(-v)}` : '0');
export const plural = (n: number, one: string, many = `${one}s`): string => `${count(n)} ${n === 1 ? one : many}`;
/** The LABEL alone, for a plate that prints the figure big and the words under it: `plural`
 *  puts the two together and cannot be used there. One ticket is not "1 tickets tested" (B1290). */
export const word = (n: number, one: string, many = `${one}s`): string => (n === 1 ? one : many);
export const days = (v: number): string => `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)} ${v === 1 ? 'day' : 'days'}`;

export const clock = (iso: string): string => new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const dayOf = (iso: string): string => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(iso));
/** "17:28" for today, "Friday 17:28" within the week, "18 September, 17:28" beyond it. */
export function when(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (dayOf(iso) === dayOf(new Date(nowMs).toISOString())) return clock(iso);
  if (nowMs - t < 6 * 86_400_000) return `${new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long' }).format(new Date(iso))} ${clock(iso)}`;
  return `${new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: 'numeric', month: 'long' }).format(new Date(iso))}, ${clock(iso)}`;
}
const yearOf = (iso: string): string => new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric' }).format(new Date(iso));
/** "Monday 14 to Friday 18 September", across two months "Monday 29 June to Sunday 20 September", one day "Wednesday 16
 *  September", and with the year wherever it is not this one: a period looked back to must say which year it was (U107). */
export function span(sinceIso: string, untilIso: string, nowMs: number = Date.now()): string {
  // A window that ends at a Monday's midnight ends on the Sunday, as a person says it.
  const until = new Date(Date.parse(untilIso) - 1).toISOString();
  const thisYear = yearOf(new Date(nowMs).toISOString());
  const years = yearOf(sinceIso) !== yearOf(until);
  const part = (iso: string, withMonth: boolean, withYear: boolean) => new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', ...(withMonth ? { month: 'long' } : {}), ...(withYear ? { year: 'numeric' } : {}) }).format(new Date(iso)).replace(',', '');   // with a year en-GB writes "Wednesday, 31 December 2025"
  const last = part(until, true, years || yearOf(until) !== thisYear);
  if (dayOf(sinceIso) === dayOf(until)) return last;
  const sameMonth = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, month: 'long', year: 'numeric' }).format(new Date(sinceIso)) === new Intl.DateTimeFormat('en-GB', { timeZone: TZ, month: 'long', year: 'numeric' }).format(new Date(until));
  return `${part(sinceIso, !sameMonth, years)} to ${last}`;
}
/** A calendar day as a person says it: "Monday 7 September", with the year when it is not this one. */
export function dayWords(day: string, nowMs: number = Date.now()): string {
  const iso = `${day}T12:00:00Z`;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', ...(day.slice(0, 4) !== yearOf(new Date(nowMs).toISOString()) ? { year: 'numeric' } : {}) }).format(new Date(iso)).replace(',', '');
}

/** Minutes as a person says them: "3h30", "45m", and nothing as a quiet 0 like every other column.
 *  Time COMMITTED to a meeting, never time spent: a calendar says who was invited, never who joined
 *  (B1467), and no caller of this may head a column "spent". */
export function hours(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m === 0) return '0';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/** Seconds as a person says a suite's running time: "38s", "2m38", "1h02". Null is the caller's to
 *  draw, never this function's: a duration nobody measured is not a duration of zero (B1466). */
export function runtime(seconds: number): string {
  const t = Math.max(0, Math.round(seconds));
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m${String(t % 60).padStart(2, '0')}`;
  return `${Math.floor(t / 3600)}h${String(Math.floor((t % 3600) / 60)).padStart(2, '0')}`;
}
